/**
 * useTooltipPositionCorrection Hook
 *
 * Custom hook to correct polaris-viz tooltip positioning in Shopify embedded apps.
 *
 * Root cause: polaris-viz uses window.scrollY and window.innerHeight for tooltip
 * Y-axis clamping in getAlteredLineChartPosition.js, but these window-relative
 * values don't work correctly in Shopify embedded app iframes where the chart
 * container may be positioned lower in the viewport.
 *
 * Solution: Detect when tooltip appears and correct its Y position by accounting
 * for the container's offset from the viewport top.
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 2.2
 */

import { useEffect, type RefObject } from 'react';

/**
 * Hook to correct polaris-viz tooltip positioning in embedded apps
 * @param containerRef - Ref to the chart container element
 */
export function useTooltipPositionCorrection(containerRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let lastCorrectedTransform = '';
    let isMouseOver = false;

    const correctTooltipPosition = () => {
      if (!isMouseOver) return;

      const tooltip = container.querySelector('[data-tooltip="true"]') as HTMLElement;
      if (!tooltip) return;

      const currentTransform = tooltip.style.transform;

      // Skip if we already corrected this transform or if there's no transform
      if (!currentTransform || currentTransform === lastCorrectedTransform) return;

      // Parse translate3d(Xpx, Ypx, Zpx) format
      const match = currentTransform.match(/translate3d\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/);
      if (!match) return;

      const [, xValue, yValue, zValue] = match;
      const currentY = parseFloat(yValue);

      // Get the chart container's position relative to viewport
      const containerRect = container.getBoundingClientRect();

      const POLARIS_TOOLTIP_MARGIN = 20; // From polaris-viz source
      const tooltipHeight = tooltip.offsetHeight || 150;

      // If tooltip Y is near the polaris-viz minimum (20px from iframe top)
      // and container is positioned down the page, we need to correct
      if (currentY < 50 && containerRect.top > 100) {
        // Apply the container offset correction
        const correctedY = currentY + containerRect.top;

        // Ensure tooltip doesn't overflow container bottom
        const maxY = containerRect.height - tooltipHeight - POLARIS_TOOLTIP_MARGIN;
        const finalY = Math.min(correctedY, maxY);

        const newTransform = `translate3d(${xValue}, ${finalY}px, ${zValue})`;
        tooltip.style.transform = newTransform;
        lastCorrectedTransform = newTransform;
      }
    };

    // Use requestAnimationFrame for smooth corrections
    let rafId: number | null = null;
    const scheduleCorrection = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        correctTooltipPosition();
      });
    };

    // Observe style changes on tooltips within the container
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target as HTMLElement;
          if (target.hasAttribute('data-tooltip')) {
            scheduleCorrection();
          }
        }
        if (mutation.type === 'childList') {
          scheduleCorrection();
        }
      }
    });

    const handleMouseEnter = () => {
      isMouseOver = true;
      lastCorrectedTransform = '';
    };

    const handleMouseLeave = () => {
      isMouseOver = false;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const handleMouseMove = () => {
      if (isMouseOver) {
        scheduleCorrection();
      }
    };

    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mousemove', handleMouseMove);

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [containerRef]);
}
